/* STAFF PURCHASE — Jay's task #1132 (21/Oct/2026), the Global Staff Purchase Procedure Rev. 3 on the portal. Max 01/11:
   "bạn làm luôn nhé". Four tabs: My purchases (the year's limit, the pledge, a request, my requests), Approvals (what
   waits for me), Ledger (Tina's monthly review, section 10 — approvers see their entities) and Setup (the module's admins:
   limits and approvers, the 18 discount categories, the employee list, the price list). Every figure is worked out by
   the server (staffPurchase.js); the form's own arithmetic is only so the person sees it while typing. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShoppingBag, CheckSquare, BookOpen, Settings, Plus, Trash2, Upload, FileSpreadsheet, X, Pencil } from 'lucide-react';
import Header from './Header';
import { BACKEND_URL } from './backendConfig';
import { SortTh } from './15_SampleTracker';
import useColumnTools from './useColumnTools';
import ModuleHelp from './ModuleHelp';
import './SPHub.css';
import './ProjectApproval.css';
import './SalesByBuyer.css';
import './StaffPurchase.css';

const API = `${BACKEND_URL}/api/staff-purchase`;
export const STAFF_PURCHASE_ON = process.env.REACT_APP_STAFF_PURCHASE === '1';

const call = async (method, path, body, raw) => {
  const res = await fetch(`${API}${path}`, {
    method, credentials: 'include',
    headers: raw ? { 'Content-Type': 'application/octet-stream' } : (body ? { 'Content-Type': 'application/json' } : undefined),
    body: raw || (body ? JSON.stringify(body) : undefined),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
  return data;
};
const ZERO = new Set(['KRW', 'JPY', 'VND']);
const money = (cur, v) => (v === null || v === undefined || v === '' ? '—'
  : `${cur} ${Number(v).toLocaleString('en-US', { minimumFractionDigits: ZERO.has(cur) ? 0 : 2, maximumFractionDigits: ZERO.has(cur) ? 0 : 2 })}`);
const roundIn = (cur, v) => (ZERO.has(cur) ? Math.round(v) : Math.round(v * 100) / 100);
const dmy = (s) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : '—');
/* THE GUIDE (Max 01/11: every module has one). A step whose control is not on the screen — the pledge once signed, the
   tabs a person does not get — is skipped by TourGuide. */
const TOUR = [
  { selector: '[data-spu-standing]', title: 'xStaffBuy.tourCardsT', body: 'xStaffBuy.tourCardsB' },
  { selector: '[data-spu-pledge]', title: 'xStaffBuy.tourPledgeT', body: 'xStaffBuy.tourPledgeB' },
  { selector: '[data-spu-form]', title: 'xStaffBuy.tourFormT', body: 'xStaffBuy.tourFormB' },
  { selector: '[data-spu-tab=queue]', title: 'xStaffBuy.tourQueueT', body: 'xStaffBuy.tourQueueB' },
  { selector: '[data-spu-tab=ledger]', title: 'xStaffBuy.tourLedgerT', body: 'xStaffBuy.tourLedgerB' },
  { selector: '[data-spu-tab=setup]', title: 'xStaffBuy.tourSetupT', body: 'xStaffBuy.tourSetupB' },
];
const blankLine = () => ({ priceId: '', productName: '', categoryId: '', qty: 1, srp: '' });

/* The procedure's Appendix, word for word, in English for every screen language: it is what the person signs, and a
   translation of a pledge with penalties in it is not ours to make (a reviewed one can be added per language later). */
const PLEDGE_EN = [
  ['p', 'I, the undersigned, understand and agree with the purpose of the Company\'s discount purchase program for employees, their acquaintances, and employees of business partners: to support my leisure activities, to build pride in the Company and its products, and to support sales activities.'],
  ['p', 'I understand that reselling any product purchased at a discount through the Company within 18 months of purchase is a breach of my obligation not to resell discounted products, regardless of whether I am still employed by the Company at the time.'],
  ['p', 'I acknowledge that the discount I receive on such purchases may be subject to taxation.'],
  ['p', 'I agree that if I violate this pledge, my discount purchase privileges will be suspended for one (1) year, and that personnel or legal action may be taken against the employees involved where necessary. I further agree that, separately from any compensation for damages the Company suffers because of my resale, the Company may claim a contractual penalty equal to ten (10) times the purchase price. I hereby submit this pledge in acknowledgement of the above.'],
  ['h', 'Tax and Local Regulatory Compliance'],
  ['p', 'I acknowledge that an employee discount may constitute a taxable benefit or employment-related benefit under the laws of the country in which I am employed.'],
  ['p', 'The Company may report or process the taxable portion of an employee discount through payroll or other applicable procedures where required by local law.'],
  ['p', 'The applicable tax treatment, exemption thresholds, valuation method, and reporting requirements will be determined in accordance with the laws and regulations applicable to my employing entity.'],
  ['p', 'I understand that the tax treatment applicable to employee purchases may differ between countries and may be amended from time to time.'],
  ['h', 'Consequences of Misuse'],
  ['p', 'If the Company determines that I have used the Employee Purchase Program for resale, proxy purchasing, stockpiling for resale, or another unauthorized purpose, the Company may, subject to applicable local law:'],
  ['li', '1. cancel or suspend my employee purchase privileges;'],
  ['li', '2. restrict or reduce my future purchase limit;'],
  ['li', '3. require repayment of the employee discount or other financial benefit improperly obtained;'],
  ['li', '4. require cancellation or return of the relevant purchase where legally and practically possible; and'],
  ['li', '5. take other appropriate action permitted under applicable law and Company policy.'],
  ['p', 'Any recovery of the employee discount or other amount will be determined based on the actual benefit obtained and the applicable local law.'],
  ['p', 'The Company will not impose any contractual penalty or recovery amount that is prohibited or restricted under applicable local law.'],
];

function StatusPill({ status, t }) {
  return <span className={`spu-pill ${status}`}>{t(`xStaffBuy.st_${status}`)}</span>;
}

/* ------------------------------------------------------------------ one request, opened */
function RequestDialog({ id, onClose, onChanged, t }) {
  const [r, setR] = useState(null);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [code, setCode] = useState('');
  const [paid, setPaid] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => call('GET', `/requests/${id}`).then((d) => {
    setR(d.request); setCode(d.request.discountCode || ''); setPaid(d.request.paidAmount ?? '');
  }).catch((e) => setErr(e.message)), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const h = (e) => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h); }, [onClose]);
  const act = async (fn) => { setBusy(true); setErr(''); try { await fn(); await load(); onChanged && onChanged(); } catch (e) { setErr(e.message); } finally { setBusy(false); } };
  return (
    <div className="spu-overlay" role="dialog" aria-modal="true" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="spu-dialog" data-spu-dialog>
        <div className="spu-dlg-head">
          <h3>{r ? `${r.no} — ${r.staff} (${r.entity})` : t('xStaffBuy.loading')}</h3>
          <button type="button" className="spu-x" onClick={onClose} aria-label={t('xStaffBuy.close')}><X size={18} /></button>
        </div>
        {err && <p className="spu-err">{err}</p>}
        {r && (
          <div className="spu-dlg-body">
            <p className="spu-meta"><StatusPill status={r.stage === 'overlimit' ? 'overlimit' : r.status} t={t} /> · {t('xStaffBuy.created', { at: dmy(String(r.createdAt).slice(0, 10)), by: r.createdBy })}
              {' · '}{t('xStaffBuy.approverIs', { who: r.approverName || '—' })}{r.routedReason ? ` (${r.routedReason})` : ''}</p>
            <table className="pa-table spu-lines">
              <thead><tr><th>{t('xStaffBuy.product')}</th><th>{t('xStaffBuy.category')}</th><th className="num">{t('xStaffBuy.qty')}</th>
                <th className="num">{t('xStaffBuy.srp')}</th><th className="num">%</th><th className="num">{t('xStaffBuy.net')}</th></tr></thead>
              <tbody>
                {r.lines.map((l, i) => (
                  <tr key={i}><td>{l.product}{l.itemCode ? <small> · {l.itemCode}</small> : null}</td><td>{l.category}</td><td className="num">{l.qty}</td>
                    <td className="num">{money(r.priceCurrency, l.srp)}</td><td className="num">{l.pct}%</td><td className="num">{money(r.priceCurrency, l.net)}</td></tr>
                ))}
              </tbody>
              <tfoot><tr><td colSpan={3}>{t('xStaffBuy.total')}</td><td className="num">{money(r.priceCurrency, r.totalSrp)}</td><td /><td className="num"><b>{money(r.priceCurrency, r.totalNet)}</b></td></tr></tfoot>
            </table>
            {r.priceCurrency !== r.currency && (
              <p className="spu-meta">{t('xStaffBuy.charged', { v: money(r.currency, r.netLocal), rate: r.fxRate, from: r.priceCurrency, to: r.currency })}</p>)}
            <p className="spu-meta">{t('xStaffBuy.remainingBefore', { v: money(r.currency, r.remainingBefore), limit: money(r.currency, r.limit) })}
              {r.channel ? ` · ${t('xStaffBuy.channel')}: ${r.channel}` : ''}</p>
            {r.overlimit && (r.overlimitAt
              ? <p className="spu-meta">✓ {t('xStaffBuy.overApproved', { by: r.overlimitBy, at: dmy(String(r.overlimitAt).slice(0, 10)) })}{r.overlimitNote ? ` — “${r.overlimitNote}”` : ''}</p>
              : <p className="spu-warn" data-spu-overlimit>{t('xStaffBuy.overNeeds')}</p>)}
            {r.note && <p className="spu-note">“{r.note}”</p>}
            {r.decidedBy && <p className="spu-meta">{t('xStaffBuy.decided', { by: r.decidedBy, at: dmy(String(r.decidedAt).slice(0, 10)) })}{r.decisionNote ? ` — “${r.decisionNote}”` : ''}</p>}

            {r.canDecide && (
              <div className="spu-decide" data-spu-decide>
                <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('xStaffBuy.notePh')} />
                <div className="spu-row">
                  <button type="button" className="pa-btn danger" disabled={busy} data-spu-reject
                    onClick={() => act(() => call('POST', `/requests/${id}/decide`, { decision: 'reject', note }))}>{t('xStaffBuy.reject')}</button>
                  <button type="button" className="pa-btn primary" disabled={busy} data-spu-approve
                    onClick={() => act(() => call('POST', `/requests/${id}/decide`, { decision: 'approve', note }))}>{r.stage === 'overlimit' ? t('xStaffBuy.approveOver') : t('xStaffBuy.approve')}</button>
                </div>
              </div>
            )}
            {r.status === 'approved' && (
              <div className="spu-after">
                <label><span>{t('xStaffBuy.code')}</span>
                  <input value={code} onChange={(e) => setCode(e.target.value)} placeholder={t('xStaffBuy.codePh')} /></label>
                <button type="button" className="pa-btn small" disabled={busy} onClick={() => act(() => call('PUT', `/requests/${id}/after`, { discountCode: code }))}>{t('xStaffBuy.save')}</button>
                <label><span>{t('xStaffBuy.paid', { cur: r.priceCurrency })}</span>
                  <input type="number" value={paid} onChange={(e) => setPaid(e.target.value)} placeholder={String(r.totalNet)} /></label>
                <button type="button" className="pa-btn small" disabled={busy} onClick={() => act(() => call('PUT', `/requests/${id}/after`, { paidAmount: paid }))}>{t('xStaffBuy.save')}</button>
                <small>{t('xStaffBuy.paidHint')}</small>
              </div>
            )}
            {r.mine && r.status === 'pending' && (
              <div className="spu-row"><button type="button" className="pa-btn" disabled={busy} data-spu-cancel
                onClick={() => act(() => call('POST', `/requests/${id}/cancel`))}>{t('xStaffBuy.cancelReq')}</button></div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* EVERY LIST IN THE MODULE (Max 01/11: "freeze header, sort và filter như excel"): the portal's own Excel header
   (SortTh + useColumnTools, as GTM, Courier and Users), inside a box that scrolls on its own so the header stays put —
   a header can only stick inside the nearest scrolling box, and a wrapper that scrolls sideways only is one.
   A column sorts and filters on what `get` returns (numbers for money, yyyy-mm-dd for dates, so both sort right);
   `cell` is what is drawn. A column without `get` (the buttons) has a plain header. Rows with their own inputs pass
   `renderRow`, so each keeps its typed values while the list is re-sorted (React follows the key). */
function ToolTable({ columns, rows, rowKey, cellsOf, renderRow, empty, t, mark, left, right }) {
  const tools = useColumnTools(rows, columns.filter((c) => c.get));
  // the list's own bar (search, hints | buttons) carries the "n of m shown" line in its middle (Max 01/11: "dời này lên")
  const info = tools.active && (
    <span className="spu-tools"><span>{t('xStaffBuy.shownOf', { n: tools.shown.length, total: rows.length })}</span>
      <button type="button" className="pa-btn small" onClick={tools.clear}>{t('xStaffBuy.clearTools')}</button></span>
  );
  const bar = (left || right || info) && <div className="spu-row wrap spu-bar">{left}{info}<span className="spu-grow" />{right}</div>;
  if (!rows.length) return <>{bar}<p className="spu-empty">{empty}</p></>;
  const cls = (c) => c.td || (c.num ? 'num' : '');
  return (
    <>
      {bar}
      <div className="spu-wrap spu-scroll"><table className="pa-table spu-table" {...(mark ? { [`data-spu-${mark}`]: '' } : {})}>
        <thead><tr>{columns.map((c) => (c.get
          ? <SortTh key={c.key} label={c.label} className={c.num ? 'num' : ''} {...tools.thProps(c.key)} />
          : <th key={c.key} className={c.num ? 'num' : ''}>{c.label}</th>))}</tr></thead>
        <tbody>
          {tools.shown.map((r) => (renderRow ? renderRow(r) : (
            <tr key={rowKey(r)} {...(cellsOf ? cellsOf(r) : {})}>
              {columns.map((c) => <td key={c.key} className={cls(c)}>{c.cell ? c.cell(r) : c.get(r)}</td>)}
            </tr>
          )))}
        </tbody>
      </table></div>
    </>
  );
}

function RequestTable({ rows, t, onOpen, who }) {
  const columns = [
    { key: 'no', label: t('xStaffBuy.no'), get: (r) => r.no },
    ...(who ? [{ key: 'staff', label: t('xStaffBuy.employee'), get: (r) => r.staff || '' },
      { key: 'entity', label: t('xStaffBuy.entity'), get: (r) => r.entity }] : []),
    { key: 'date', label: t('xStaffBuy.date'), get: (r) => String(r.createdAt || '').slice(0, 10), cell: (r) => dmy(String(r.createdAt || '').slice(0, 10)) },
    { key: 'items', label: t('xStaffBuy.items'), get: (r) => r.lines.map((l) => `${l.qty}× ${l.product}`).join(', '), td: 'spu-items' },
    { key: 'net', label: t('xStaffBuy.net'), num: true, get: (r) => r.paidLocal ?? r.netLocal, cell: (r) => money(r.currency, r.paidLocal ?? r.netLocal) },
    { key: 'status', label: t('xStaffBuy.status'), get: (r) => t(`xStaffBuy.st_${r.stage === 'overlimit' ? 'overlimit' : r.status}`),
      cell: (r) => <StatusPill status={r.stage === 'overlimit' ? 'overlimit' : r.status} t={t} /> },
    { key: 'approver', label: t('xStaffBuy.approver'), get: (r) => r.decidedBy || r.approverName || '' },
    { key: 'code', label: t('xStaffBuy.code'), get: (r) => r.discountCode || '' },
    { key: 'action', label: t('xStaffBuy.action'), cell: (r) => (
      <button type="button" className={`pa-btn small${r.canDecide ? ' primary' : ''}`} onClick={() => onOpen(r.id)}>
        {r.canDecide ? t('xStaffBuy.review') : t('xStaffBuy.open')}</button>) },
  ];
  return <ToolTable columns={columns} rows={rows} rowKey={(r) => r.id} cellsOf={(r) => ({ 'data-spu-row': r.no })} empty={t('xStaffBuy.noRequests')} t={t} />;
}

/* ------------------------------------------------------------------ My purchases */
function MineTab({ me, reload, t, onOpen }) {
  const st = me.standing;
  const [mine, setMine] = useState([]);
  const [lines, setLines] = useState([blankLine()]);
  const [channel, setChannel] = useState('');
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [sign, setSign] = useState({ name: '', birth: '', agree: false });
  const [ccy, setCcy] = useState('');   // KRW for HEVN, HCHN and HEMY buying on the HINC website (Final, local process table)
  const loadMine = useCallback(() => call('GET', `/requests?scope=mine&year=${me.year}`).then((d) => setMine(d.requests)).catch(() => {}), [me.year]);
  useEffect(() => { loadMine(); }, [loadMine]);

  const cats = useMemo(() => new Map(me.categories.map((c) => [String(c.id), c])), [me.categories]);
  const prices = useMemo(() => new Map((me.prices || []).map((p) => [String(p.id), p])), [me.prices]);
  const cur = st?.currency || '';
  const pc = ccy || cur;                                   // what the SRPs are in
  const rate = pc === cur ? 1 : st?.krwRate || null;       // the year's fixed rate into the entity's currency
  const shownPrices = (me.prices || []).filter((p) => (p.currency || cur) === pc);
  const priced = lines.map((l) => {
    const p = prices.get(String(l.priceId));
    const c = cats.get(String(p ? p.categoryId : l.categoryId));
    const srp = p ? p.srp : Number(l.srp);
    const pct = c ? c.pct : null;
    const unit = pct === null || !(srp > 0) ? null : roundIn(pc, srp * (1 - pct / 100));
    return { ...l, p, c, srp, pct, unit, net: unit === null ? null : roundIn(pc, unit * (Number(l.qty) || 0)) };
  });
  const total = roundIn(pc, priced.reduce((s, l) => s + (l.net || 0), 0));
  const local = rate ? roundIn(cur, total * rate) : null;
  // over the limit is asked, not refused (Final 26/Oct, section 4): it waits for the over-limit approval first
  const over = !!st && local !== null && local > st.remaining;
  const setLine = (i, patch) => setLines((ls) => ls.map((l, k) => (k === i ? { ...l, ...patch } : l)));

  if (!me.staff) return <div className="spu-card warn" data-spu-nostaff>{t('xStaffBuy.notOnList')}</div>;

  const submit = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      const d = await call('POST', '/requests', {
        lines: lines.map((l) => ({ priceId: l.priceId || undefined, productName: l.productName, categoryId: l.categoryId ? Number(l.categoryId) : undefined, qty: Number(l.qty), srp: l.srp === '' ? undefined : Number(l.srp) })),
        currency: pc, channel: channel || (pc === 'KRW' ? 'HINC website' : `${me.staff.entity} website`), note,
      });
      setMsg(t('xStaffBuy.sent', { no: d.no })); setLines([blankLine()]); setNote(''); reload(); loadMine();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const signPledge = async () => {
    setBusy(true); setErr('');
    try { await call('POST', '/pledge', { signedName: sign.name, birthDate: sign.birth || undefined, agree: sign.agree }); reload(); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="spu-mine">
      <div className="spu-cards" data-spu-standing>
        <div className="spu-card"><span>{t('xStaffBuy.limitYear', { y: me.year })}</span><b>{money(cur, st.limit)}</b>
          <em>{[st.senior ? t('xStaffBuy.seniorNote') : null,
            st.limitCurrency !== cur ? t('xStaffBuy.limitFrom', { v: money(st.limitCurrency, st.senior ? st.seniorLimit : st.baseLimit) }) : null,
            st.limit < roundIn(cur, (st.senior ? st.seniorLimit : st.baseLimit) * (st.limitRate || 1)) ? t('xStaffBuy.proratedShort') : null,
          ].filter(Boolean).join(' · ') || `${me.staff.entity} · ${cur}`}</em></div>
        <div className="spu-card"><span>{t('xStaffBuy.approved')}</span><b>{money(cur, st.approved)}</b><em>{st.pending ? t('xStaffBuy.pendingHeld', { v: money(cur, st.pending) }) : ' '}</em></div>
        <div className="spu-card main"><span>{t('xStaffBuy.remaining')}</span><b>{money(cur, st.remaining)}</b><em>{t('xStaffBuy.approverIs', { who: st.approverName || '—' })}</em></div>
        <div className="spu-card"><span>{t('xStaffBuy.eligible')}</span><b>{dmy(st.eligible)}</b><em>{st.isEligible ? t('xStaffBuy.eligibleYes') : t('xStaffBuy.eligibleNo')}</em></div>
      </div>
      {st.onHold && <div className="spu-card warn" data-spu-onhold>{t('xStaffBuy.onHold', { e: st.entity })}</div>}
      {st.fxMissing && <div className="spu-card warn" data-spu-fxmissing>{t('xStaffBuy.fxMissing', { y: me.year, from: st.limitCurrency, to: cur })}</div>}
      {err && <p className="spu-err">{err}</p>}
      {msg && <p className="spu-ok">{msg}</p>}

      {!me.pledge ? (
        <section className="spu-section" data-spu-pledge>
          <h3>{t('xStaffBuy.pledgeTitle')}</h3>
          <p className="spu-hint">{t('xStaffBuy.pledgeEnNote', { v: me.pledgeVersion || '' })}</p>
          <div className="spu-pledge" lang="en">
            {PLEDGE_EN.map(([kind, text], i) => (kind === 'h' ? <h4 key={i}>{text}</h4> : kind === 'li' ? <p key={i} className="li">{text}</p> : <p key={i}>{text}</p>))}
          </div>
          <div className="spu-row wrap">
            <label><span>{t('xStaffBuy.fullName')}</span><input value={sign.name} onChange={(e) => setSign({ ...sign, name: e.target.value })} placeholder={me.staff.name} /></label>
            <label><span>{t('xStaffBuy.birth')}</span><input type="date" value={sign.birth} onChange={(e) => setSign({ ...sign, birth: e.target.value })} /></label>
            <label className="spu-check"><input type="checkbox" checked={sign.agree} onChange={(e) => setSign({ ...sign, agree: e.target.checked })} /> {t('xStaffBuy.agree')}</label>
            <button type="button" className="pa-btn primary" disabled={busy || !sign.agree || sign.name.trim().length < 2} onClick={signPledge} data-spu-sign>{t('xStaffBuy.sign')}</button>
          </div>
        </section>
      ) : (
        <p className="spu-meta">✓ {t('xStaffBuy.pledgeSigned', { at: dmy(String(me.pledge.signed_at).slice(0, 10)), v: me.pledge.version })}</p>
      )}

      {me.pledge && st.isEligible && !st.onHold && !st.fxMissing && (
        <section className="spu-section" data-spu-form>
          <h3>{t('xStaffBuy.newRequest')}</h3>
          {st.krwPurchase && (
            <div className="spu-row wrap">
              <label><span>{t('xStaffBuy.priceIn')}</span>
                <select value={pc} onChange={(e) => { setCcy(e.target.value === cur ? '' : e.target.value); setLines([blankLine()]); }} data-spu-ccy>
                  <option value={cur}>{cur}</option><option value="KRW">{t('xStaffBuy.krwOption')}</option>
                </select></label>
              {pc !== cur && <p className="spu-hint">{rate ? t('xStaffBuy.krwRate', { y: me.year, rate: rate.toLocaleString('en-US', { maximumFractionDigits: 6 }), to: cur }) : t('xStaffBuy.krwRateMissing', { y: me.year, to: cur })}</p>}
            </div>
          )}
          {!shownPrices.length && <p className="spu-hint">{t('xStaffBuy.noPriceList')}</p>}
          <div className="spu-wrap"><table className="pa-table spu-form">
            <thead><tr><th>{t('xStaffBuy.product')}</th><th>{t('xStaffBuy.category')}</th><th className="num">{t('xStaffBuy.qty')}</th>
              <th className="num">{t('xStaffBuy.srpCur', { cur: pc })}</th><th className="num">%</th><th className="num">{t('xStaffBuy.net')}</th><th /></tr></thead>
            <tbody>
              {priced.map((l, i) => (
                <tr key={i}>
                  <td>
                    {shownPrices.length > 0 && (
                      <select value={l.priceId} onChange={(e) => setLine(i, { priceId: e.target.value })}>
                        <option value="">{t('xStaffBuy.typeItem')}</option>
                        {shownPrices.map((p) => <option key={p.id} value={p.id} disabled={p.limited}>{p.name}{p.color ? ` — ${p.color}` : ''}{p.limited ? ` (${t('xStaffBuy.limited')})` : ''}</option>)}
                      </select>
                    )}
                    {!l.priceId && <input value={l.productName} onChange={(e) => setLine(i, { productName: e.target.value })} placeholder={t('xStaffBuy.productPh')} />}
                  </td>
                  <td>{l.p ? (l.c?.name || '—') : (
                    <select value={l.categoryId} onChange={(e) => setLine(i, { categoryId: e.target.value })}>
                      <option value="">{t('xStaffBuy.pickCategory')}</option>
                      {me.categories.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.pct}%</option>)}
                    </select>)}</td>
                  <td className="num"><input type="number" min={1} value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} className="spu-qty" /></td>
                  <td className="num">{l.p ? money(pc, l.srp) : <input type="number" min={0} value={l.srp} onChange={(e) => setLine(i, { srp: e.target.value })} className="spu-srp" />}</td>
                  <td className="num">{l.pct === null ? '—' : `${l.pct}%`}</td>
                  <td className="num">{l.net === null ? '—' : money(pc, l.net)}</td>
                  <td>{lines.length > 1 && <button type="button" className="spu-icon" onClick={() => setLines((ls) => ls.filter((_, k) => k !== i))} aria-label={t('xStaffBuy.remove')}><Trash2 size={14} /></button>}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr><td colSpan={5}><button type="button" className="pa-btn small" onClick={() => setLines((ls) => [...ls, blankLine()])}><Plus size={13} /> {t('xStaffBuy.addItem')}</button></td>
              <td className="num"><b>{money(pc, total)}</b></td><td /></tr></tfoot>
          </table></div>
          <p className={`spu-meta${over ? ' spu-neg' : ''}`} data-spu-after>
            {pc !== cur && local !== null ? `${t('xStaffBuy.chargedShort', { v: money(cur, local) })} · ` : ''}
            {local === null ? '' : t('xStaffBuy.afterThis', { v: money(cur, roundIn(cur, st.remaining - local)) })}</p>
          {over && <p className="spu-warn" data-spu-overnote>{t('xStaffBuy.overNote')}</p>}
          <div className="spu-row wrap">
            <label><span>{t('xStaffBuy.channel')}</span><input value={channel} onChange={(e) => setChannel(e.target.value)} placeholder={`${me.staff.entity} website`} /></label>
            <label className="grow"><span>{t('xStaffBuy.note')}</span><input value={note} onChange={(e) => setNote(e.target.value)} /></label>
            <button type="button" className="pa-btn primary" disabled={busy || !total || local === null} onClick={submit} data-spu-submit>{over ? t('xStaffBuy.submitOver') : t('xStaffBuy.submit')}</button>
          </div>
        </section>
      )}

      <section className="spu-section">
        <h3>{t('xStaffBuy.myRequests', { y: me.year })}</h3>
        <RequestTable rows={mine} t={t} onOpen={onOpen} />
      </section>
    </div>
  );
}

/* ------------------------------------------------------------------ Approvals */
function QueueTab({ t, onOpen, stamp }) {
  const [rows, setRows] = useState(null);
  const [all, setAll] = useState(null);
  useEffect(() => {
    call('GET', '/requests?scope=queue').then((d) => setRows(d.requests)).catch(() => setRows([]));
    call('GET', '/requests?scope=all').then((d) => setAll(d.requests)).catch(() => setAll([]));
  }, [stamp]);
  return (
    <div>
      <section className="spu-section"><h3>{t('xStaffBuy.waitingForYou')}</h3>{rows ? <RequestTable rows={rows} t={t} onOpen={onOpen} who /> : <p>{t('xStaffBuy.loading')}</p>}</section>
      <section className="spu-section"><h3>{t('xStaffBuy.allRequests')}</h3>{all ? <RequestTable rows={all} t={t} onOpen={onOpen} who /> : <p>{t('xStaffBuy.loading')}</p>}</section>
    </div>
  );
}

/* ------------------------------------------------------------------ Ledger (section 10) */
function LedgerTab({ t, year }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => { call('GET', `/ledger?year=${year}`).then(setData).catch((e) => setErr(e.message)); }, [year]);
  if (err) return <p className="spu-err">{err}</p>;
  if (!data) return <p>{t('xStaffBuy.loading')}</p>;
  // the entity picker that stood here is the Entity column's funnel now
  const columns = [
    { key: 'entity', label: t('xStaffBuy.entity'), get: (r) => r.entity },
    { key: 'name', label: t('xStaffBuy.employee'), get: (r) => r.name },
    { key: 'senior', label: 'Sr Mgr+', get: (r) => (r.senior ? 'Sr Mgr+' : ''), cell: (r) => (r.senior ? '✓' : '') },
    { key: 'eligible', label: t('xStaffBuy.eligible'), get: (r) => r.eligible || '', cell: (r) => dmy(r.eligible) },
    { key: 'limit', label: t('xStaffBuy.limitYear', { y: year }), num: true, get: (r) => r.limit, cell: (r) => money(r.currency, r.limit) },
    { key: 'approved', label: t('xStaffBuy.approved'), num: true, get: (r) => r.approved, cell: (r) => (r.approved ? money(r.currency, r.approved) : '—') },
    { key: 'pending', label: t('xStaffBuy.pending'), num: true, get: (r) => r.pending, cell: (r) => (r.pending ? money(r.currency, r.pending) : '—') },
    { key: 'remaining', label: t('xStaffBuy.remaining'), num: true, get: (r) => r.remaining, cell: (r) => (r.remaining === null ? '—' : money(r.currency, r.remaining)) },
    { key: 'linked', label: t('xStaffBuy.account'), get: (r) => (r.linked ? '✓' : t('xStaffBuy.notLinked')), cell: (r) => (r.linked ? '✓' : <span className="spu-dim">{t('xStaffBuy.notLinked')}</span>) },
  ];
  return (
    <div>
      <ToolTable columns={columns} rows={data.rows} rowKey={(r) => r.id} empty={t('xStaffBuy.noRequests')} t={t} mark="ledger"
        right={<a className="pa-btn" href={`${API}/ledger.xlsx?year=${year}`}><FileSpreadsheet size={14} /> Excel</a>} />
    </div>
  );
}

/* ------------------------------------------------------------------ Setup (the module's admins) */
/* CREATE · READ · UPDATE · DELETE (Max 01/11). Each list reads only; a row changes through an Action column of labelled
   buttons (Edit, Delete — the portal's rule, never a "…" menu), "Add" sits above the list, and both open one centred
   form. Delete asks first; the server refuses a row that something still uses and says to switch it off instead. */
/* A select you can type into (Max 01/11: the account list is ~90 long — "cho search nha"). Typing filters on the whole
   label (name and e-mail); arrows + Enter pick, Escape closes the list only (the form's own Escape is on window, so the
   key is stopped here). */
function SearchPick({ value, options, onChange, placeholder, field }) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [hi, setHi] = useState(0);
  const listRef = useRef(null);
  const current = options.find(([v]) => String(v) === String(value ?? ''));
  const words = term.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const shown = options.filter(([, lab]) => words.every((w) => lab.toLowerCase().includes(w)));
  const pick = (v) => { onChange(v); setOpen(false); setTerm(''); };
  useEffect(() => { setHi(0); }, [term]);
  useEffect(() => {
    const el = listRef.current && listRef.current.children[hi];
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [hi, open]);
  const key = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHi((h) => Math.min(h + 1, shown.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { if (open && shown[hi]) { e.preventDefault(); pick(shown[hi][0]); } }
    else if (e.key === 'Escape' && open) { e.preventDefault(); e.nativeEvent.stopPropagation(); setOpen(false); setTerm(''); }
  };
  return (
    <div className="spu-pick">
      <input value={open ? term : (current ? current[1] : '')} placeholder={open ? (current ? current[1] : placeholder) : placeholder}
        onFocus={() => setOpen(true)} onChange={(e) => { setTerm(e.target.value); setOpen(true); }} onKeyDown={key}
        onBlur={() => setTimeout(() => { setOpen(false); setTerm(''); }, 150)} data-field={field} role="combobox" aria-expanded={open} aria-controls={`spu-pick-${field}`} autoComplete="off" />
      {open && (
        <ul className="spu-pick-list" id={`spu-pick-${field}`} ref={listRef} role="listbox">
          {shown.map(([v, lab], i) => (
            <li key={v} role="option" aria-selected={String(v) === String(value ?? '')} className={`${i === hi ? 'hi' : ''}${String(v) === String(value ?? '') ? ' on' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); pick(v); }} onMouseEnter={() => setHi(i)}>{lab}</li>
          ))}
          {!shown.length && <li className="none">—</li>}
        </ul>
      )}
    </div>
  );
}

function FormDialog({ title, fields, initial, onSubmit, onClose, t }) {
  const [v, setV] = useState(initial);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { const h = (e) => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h); }, [onClose]);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr('');
    try { await onSubmit(v); onClose(); } catch (x) { setErr(x.message); setBusy(false); }
  };
  return (
    <div className="spu-overlay" role="dialog" aria-modal="true" aria-label={title} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form className="spu-dialog spu-form-dlg" onSubmit={submit} data-spu-formdlg>
        <div className="spu-dlg-head"><h3>{title}</h3>
          <button type="button" className="spu-x" onClick={onClose} aria-label={t('xStaffBuy.close')}><X size={18} /></button></div>
        <div className="spu-dlg-body">
          <div className="spu-fields">
            {fields.map((f) => (
              <label key={f.key} className={`spu-field${f.type === 'check' ? ' check' : ''}${f.wide ? ' wide' : ''}`}>
                {f.type === 'check' ? (
                  <><input type="checkbox" checked={!!v[f.key]} onChange={(e) => setV({ ...v, [f.key]: e.target.checked })} data-field={f.key} /> {f.label}</>
                ) : (
                  <>
                    <span>{f.label}{f.required ? <b className="spu-req"> *</b> : null}</span>
                    {f.type === 'pick' ? (
                      <SearchPick value={v[f.key]} options={f.options} onChange={(x) => setV({ ...v, [f.key]: x })} placeholder={f.placeholder} field={f.key} />
                    ) : f.type === 'select' ? (
                      <select value={v[f.key] ?? ''} onChange={(e) => setV({ ...v, [f.key]: e.target.value })} disabled={f.disabled} required={f.required} data-field={f.key}>
                        {f.options.map(([val, lab]) => <option key={val} value={val}>{lab}</option>)}
                      </select>
                    ) : f.type === 'textarea' ? (
                      <textarea rows={2} value={v[f.key] ?? ''} onChange={(e) => setV({ ...v, [f.key]: e.target.value })} data-field={f.key} />
                    ) : (
                      <input type={f.type || 'text'} value={v[f.key] ?? ''} onChange={(e) => setV({ ...v, [f.key]: e.target.value })} disabled={f.disabled}
                        required={f.required} placeholder={f.placeholder} min={f.min} max={f.max} step={f.step} data-field={f.key} />
                    )}
                    {f.hint && <small>{f.hint}</small>}
                  </>
                )}
              </label>
            ))}
          </div>
          {err && <p className="spu-err">{err}</p>}
          <div className="spu-row spu-dlg-foot">
            <button type="button" className="pa-btn" onClick={onClose}>{t('xStaffBuy.cancel')}</button>
            <button type="submit" className="pa-btn primary" disabled={busy} data-spu-formsave>{t('xStaffBuy.save')}</button>
          </div>
        </div>
      </form>
    </div>
  );
}
function ConfirmDelete({ what, onConfirm, onClose, t }) {
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { const h = (e) => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h); }, [onClose]);
  const go = async () => { setBusy(true); setErr(''); try { await onConfirm(); onClose(); } catch (x) { setErr(x.message); setBusy(false); } };
  return (
    <div className="spu-overlay" role="alertdialog" aria-modal="true" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="spu-dialog spu-confirm" data-spu-confirm>
        <div className="spu-dlg-head danger"><h3>{t('xStaffBuy.deleteTitle')}</h3>
          <button type="button" className="spu-x" onClick={onClose} aria-label={t('xStaffBuy.close')}><X size={18} /></button></div>
        <div className="spu-dlg-body">
          <p><b>{what}</b></p>
          <p className="spu-meta">{t('xStaffBuy.deleteBody')}</p>
          {err && <p className="spu-err">{err}</p>}
          <div className="spu-row spu-dlg-foot">
            <button type="button" className="pa-btn" onClick={onClose}>{t('xStaffBuy.cancel')}</button>
            <button type="button" className="pa-btn primary spu-danger-fill" disabled={busy || !!err} onClick={go} data-spu-confirmdel>{t('xStaffBuy.delete')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
const OnOff = ({ on, t }) => <span className={`spu-pill ${on ? 'approved' : 'cancelled'}`}>{on ? t('xStaffBuy.on') : t('xStaffBuy.off')}</span>;

function SetupTab({ t }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [part, setPart] = useState('entities');
  const [q, setQ] = useState('');
  const [form, setForm] = useState(null);     // { title, fields, initial, submit }
  const [del, setDel] = useState(null);       // { what, run }
  const staffFile = useRef(null); const priceFile = useRef(null);
  const load = useCallback(() => call('GET', '/setup').then(setD).catch((e) => setErr(e.message)), []);
  useEffect(() => { load(); }, [load]);
  const save = async (fn, okMsg) => { setErr(''); setMsg(''); try { const r = await fn(); setMsg(okMsg(r)); load(); } catch (e) { setErr(e.message); } };
  const upload = (path, file, okMsg) => file && save(async () => call('POST', `${path}?file=${encodeURIComponent(file.name)}`, null, await file.arrayBuffer()), okMsg);
  // the dialog's own call: an error stays in the dialog (thrown), success refreshes the list and says so
  const write = (method, path) => async (body) => { await call(method, path, body); setErr(''); setMsg(t('xStaffBuy.saved')); load(); };
  const remove = (path, what) => setDel({ what, run: async () => { const r = await call('DELETE', path); setErr(''); setMsg(r.hidden ? t('xStaffBuy.hidden') : t('xStaffBuy.deleted')); load(); } });
  if (!d) return err ? <p className="spu-err">{err}</p> : <p>{t('xStaffBuy.loading')}</p>;

  const entityOpts = d.entities.map((e) => [e.entity, `${e.entity}${e.region ? ` · ${e.region}` : ''}`]);
  const catOpts = [['', '—'], ...d.categories.map((c) => [String(c.id), `${c.name} · ${c.pct}%`])];
  const userOpts = [['', t('xStaffBuy.notLinked')], ...d.users.map((u) => [String(u.id), `${u.name} · ${u.email}`])];
  const actions = (onEdit, onDelete) => (
    <span className="spu-actions">
      <button type="button" className="pa-btn small spu-edit" onClick={onEdit} data-spu-edit><Pencil size={13} /> {t('xStaffBuy.edit')}</button>
      <button type="button" className="pa-btn small danger" onClick={onDelete} data-spu-del><Trash2 size={13} /> {t('xStaffBuy.delete')}</button>
    </span>
  );

  const entityFields = (isNew) => [
    { key: 'entity', label: t('xStaffBuy.entityCode'), required: true, disabled: !isNew, placeholder: 'HAUS' },
    { key: 'region', label: t('xStaffBuy.region'), placeholder: 'Australia' },
    { key: 'currency', label: t('xStaffBuy.currency'), required: true, placeholder: 'AUD' },
    { key: 'annual_limit', label: t('xStaffBuy.annualLimit'), type: 'number', required: true, min: 0, step: 'any' },
    { key: 'senior_limit', label: t('xStaffBuy.seniorLimit'), type: 'number', min: 0, step: 'any', hint: t('xStaffBuy.seniorHint') },
    { key: 'limit_currency', label: t('xStaffBuy.limitCurrency'), placeholder: 'MYR', hint: t('xStaffBuy.limitCcyHint') },
    { key: 'approver_emails', label: t('xStaffBuy.approverEmails'), wide: true, placeholder: 'name@helinox.com, name2@helinox.com', hint: t('xStaffBuy.approverHint') },
    { key: 'approver_name', label: t('xStaffBuy.approverName'), wide: true },
    { key: 'manager_emails', label: t('xStaffBuy.managerEmails'), wide: true, placeholder: 'name@helinox.com', hint: t('xStaffBuy.managerHint') },
    { key: 'krw_purchase', label: t('xStaffBuy.krwPurchase'), type: 'check' },
    { key: 'active', label: t('xStaffBuy.active'), type: 'check' },
  ];
  const openEntity = (e) => setForm({
    title: e ? `${t('xStaffBuy.editEntity')} — ${e.entity}` : t('xStaffBuy.addEntity'),
    fields: entityFields(!e),
    initial: e ? { entity: e.entity, region: e.region || '', currency: e.currency, annual_limit: e.annual_limit, approver_emails: e.approver_emails || '', approver_name: e.approver_name || '',
      senior_limit: e.senior_limit ?? '', limit_currency: e.limit_currency || '', manager_emails: e.manager_emails || '', krw_purchase: !!e.krw_purchase,
      active: e.is_active !== false && e.is_active !== 0 }
      : { entity: '', region: '', currency: '', annual_limit: '', approver_emails: '', approver_name: '', senior_limit: '', limit_currency: '', manager_emails: '', krw_purchase: false, active: true },
    submit: e ? write('PUT', `/setup/entity/${e.entity}`) : write('POST', '/setup/entity'),
  });
  const openCategory = (c) => setForm({
    title: c ? t('xStaffBuy.editCategory') : t('xStaffBuy.addCategory'),
    fields: [
      { key: 'name', label: t('xStaffBuy.category'), required: true, wide: true },
      { key: 'pct', label: `${t('xStaffBuy.discount')} (%)`, type: 'number', required: true, min: 0, max: 100, step: 'any' },
      { key: 'sort', label: t('xStaffBuy.sortOrder'), type: 'number', step: 1, hint: t('xStaffBuy.sortHint') },
      { key: 'active', label: t('xStaffBuy.active'), type: 'check' },
    ],
    initial: c ? { name: c.name, pct: c.pct, sort: c.sort, active: c.active } : { name: '', pct: '', sort: (Math.max(0, ...d.categories.map((x) => x.sort || 0)) + 10), active: true },
    submit: c ? write('PUT', `/setup/category/${c.id}`) : write('POST', '/setup/category'),
  });
  const openStaff = (s) => setForm({
    title: s ? `${t('xStaffBuy.editStaff')} — ${s.name}` : t('xStaffBuy.addStaff'),
    fields: [
      { key: 'full_name', label: t('xStaffBuy.fullNameField'), required: true, wide: true },
      { key: 'entity', label: t('xStaffBuy.entity'), type: 'select', required: true, options: [['', '—'], ...entityOpts] },
      { key: 'date_joined', label: t('xStaffBuy.joined'), type: 'date', hint: t('xStaffBuy.joinedHint') },
      { key: 'department', label: t('xStaffBuy.department') },
      { key: 'job_title', label: t('xStaffBuy.jobTitle') },
      { key: 'email', label: 'E-mail', type: 'email', placeholder: 'name@helinox.com' },
      { key: 'userId', label: t('xStaffBuy.account'), type: 'pick', options: userOpts, wide: true, placeholder: t('xStaffBuy.searchAccount') },
      { key: 'remarks', label: t('xStaffBuy.remarks'), type: 'textarea', wide: true },
      { key: 'senior', label: t('xStaffBuy.seniorField'), type: 'check' },
      { key: 'active', label: t('xStaffBuy.active'), type: 'check' },
    ],
    initial: s ? { full_name: s.name, entity: s.entity, date_joined: s.joined || '', department: s.department || '', job_title: s.title || '', email: s.email || '',
      userId: s.userId ? String(s.userId) : '', remarks: s.remarks || '', senior: s.senior, active: s.active }
      : { full_name: '', entity: '', date_joined: '', department: '', job_title: '', email: '', userId: '', remarks: '', senior: false, active: true },
    submit: s ? write('PUT', `/setup/staff/${s.id}`) : write('POST', '/setup/staff'),
  });
  /* THE YEAR'S FIXED RATES (Jay 02/11): set each January, used all year — HEMY's MYR limit shown in SGD, and the KRW
     the HEVN, HCHN and HEMY staff pay on the HINC website charged to their limit. A request keeps the rate it was asked at. */
  const fxKey = (r) => `${r.year}/${r.from}/${r.to}`;
  const openFx = (r) => setForm({
    title: r ? `${t('xStaffBuy.editFx')} — ${r.year} ${r.from} → ${r.to}` : t('xStaffBuy.addFx'),
    fields: [
      { key: 'year', label: t('xStaffBuy.fxYear'), type: 'number', required: true, min: 2024, max: 2100, step: 1, disabled: !!r },
      { key: 'from', label: t('xStaffBuy.fxFrom'), required: true, placeholder: 'KRW', disabled: !!r },
      { key: 'to', label: t('xStaffBuy.fxTo'), required: true, placeholder: 'VND', disabled: !!r },
      { key: 'rate', label: t('xStaffBuy.fxRate'), type: 'number', required: true, min: 0, step: 'any', hint: t('xStaffBuy.fxRateHint') },
      { key: 'note', label: t('xStaffBuy.fxNote'), wide: true },
    ],
    initial: r ? { year: r.year, from: r.from, to: r.to, rate: r.rate, note: r.note || '' } : { year: new Date().getFullYear(), from: 'KRW', to: '', rate: '', note: '' },
    submit: r ? write('PUT', `/setup/fx/${fxKey(r)}`) : write('POST', '/setup/fx'),
  });
  const openPrice = (p) => setForm({
    title: p ? `${t('xStaffBuy.editPrice')} — ${p.code}` : t('xStaffBuy.addPrice'),
    fields: [
      { key: 'entity', label: t('xStaffBuy.entity'), type: 'select', required: true, options: [['', '—'], ...entityOpts] },
      { key: 'code', label: t('xStaffBuy.itemCode'), required: true },
      { key: 'name', label: t('xStaffBuy.product'), required: true, wide: true },
      { key: 'color', label: t('xStaffBuy.color') },
      { key: 'categoryId', label: t('xStaffBuy.category'), type: 'select', options: catOpts },
      { key: 'srp', label: 'SRP', type: 'number', required: true, min: 0, step: 'any', hint: t('xStaffBuy.srpHint') },
      { key: 'currency', label: t('xStaffBuy.priceCurrency'), placeholder: 'KRW', hint: t('xStaffBuy.priceCcyHint') },
      { key: 'limited', label: t('xStaffBuy.limited'), type: 'check' },
    ],
    initial: p ? { entity: p.entity, code: p.code, name: p.name, color: p.color || '', categoryId: p.categoryId ? String(p.categoryId) : '', srp: p.srp, currency: p.currency || '', limited: p.limited }
      : { entity: '', code: '', name: '', color: '', categoryId: '', srp: '', currency: '', limited: false },
    submit: p ? write('PUT', `/setup/price/${p.id}`) : write('POST', '/setup/price'),
  });

  const staff = d.staff.filter((s) => !q || `${s.name} ${s.entity} ${s.email || ''} ${s.userName || ''}`.toLowerCase().includes(q.toLowerCase()));
  const addBtn = (label, onClick) => <button type="button" className="pa-btn primary" onClick={onClick} data-spu-add><Plus size={14} /> {label}</button>;
  return (
    <div>
      <div className="spu-subtabs">
        {['entities', 'categories', 'staff', 'prices', 'fx'].map((p) => (
          <button key={p} type="button" className={part === p ? 'on' : ''} onClick={() => { setPart(p); setMsg(''); setErr(''); }}>{t(`xStaffBuy.setup_${p}`)}</button>))}
      </div>
      {err && <p className="spu-err">{err}</p>}
      {msg && <p className="spu-ok">{msg}</p>}
      {part === 'entities' && (
        <div>
          <ToolTable t={t} rows={d.entities} rowKey={(e) => e.entity} empty="—" mark="entities" right={addBtn(t('xStaffBuy.addEntity'), () => openEntity(null))} columns={[
            { key: 'entity', label: t('xStaffBuy.entity'), get: (e) => e.entity, cell: (e) => <><b>{e.entity}</b>{e.region ? <small> · {e.region}</small> : null}</> },
            { key: 'currency', label: t('xStaffBuy.currency'), get: (e) => e.currency },
            { key: 'limit', label: t('xStaffBuy.annualLimit'), num: true, get: (e) => e.annual_limit, cell: (e) => money(e.limit_currency || e.currency, e.annual_limit) },
            { key: 'senior', label: t('xStaffBuy.seniorLimit'), num: true, get: (e) => e.senior_limit ?? e.annual_limit * 2,
              cell: (e) => money(e.limit_currency || e.currency, e.senior_limit ?? e.annual_limit * 2) },
            { key: 'krw', label: 'KRW', get: (e) => (e.krw_purchase ? '✓' : ''), cell: (e) => (e.krw_purchase ? '✓' : '') },
            { key: 'emails', label: t('xStaffBuy.approverEmails'), get: (e) => e.approver_emails || '', cell: (e) => e.approver_emails || <span className="spu-dim">{t('xStaffBuy.noApprover')}</span> },
            { key: 'aname', label: t('xStaffBuy.approverName'), get: (e) => e.approver_name || '' },
            { key: 'managers', label: t('xStaffBuy.managerEmails'), get: (e) => e.manager_emails || '', cell: (e) => e.manager_emails || <span className="spu-dim">—</span> },
            { key: 'active', label: t('xStaffBuy.active'), get: (e) => (e.is_active ? t('xStaffBuy.on') : t('xStaffBuy.off')), cell: (e) => <OnOff on={!!e.is_active} t={t} /> },
            { key: 'action', label: t('xStaffBuy.action'), cell: (e) => actions(() => openEntity(e), () => remove(`/setup/entity/${e.entity}`, `${e.entity}${e.region ? ` · ${e.region}` : ''}`)) },
          ]} />
          <p className="spu-hint">{t('xStaffBuy.settingsNote', { self: d.settings['staffbuy.self_approver'] || '—', admins: d.settings['staffbuy.admins'] || '—', mail: d.settings['staffbuy.mail_live'] === '1' ? 'ON' : 'OFF' })}</p>
        </div>
      )}
      {part === 'categories' && (
        <div>
          <ToolTable t={t} rows={d.categories} rowKey={(c) => c.id} empty="—" mark="categories"
            left={<p className="spu-hint">{t('xStaffBuy.categoryHint')}</p>} right={addBtn(t('xStaffBuy.addCategory'), () => openCategory(null))} columns={[
            { key: 'sort', label: '#', num: true, get: (c) => c.sort },
            { key: 'name', label: t('xStaffBuy.category'), get: (c) => c.name },
            { key: 'pct', label: t('xStaffBuy.discount'), num: true, get: (c) => c.pct, cell: (c) => `${c.pct}%` },
            { key: 'active', label: t('xStaffBuy.active'), get: (c) => (c.active ? t('xStaffBuy.on') : t('xStaffBuy.off')), cell: (c) => <OnOff on={c.active} t={t} /> },
            { key: 'action', label: t('xStaffBuy.action'), cell: (c) => actions(() => openCategory(c), () => remove(`/setup/category/${c.id}`, `${c.name} · ${c.pct}%`)) },
          ]} />
        </div>
      )}
      {part === 'staff' && (
        <div>
          <ToolTable t={t} rows={staff} rowKey={(s) => s.id} empty="—" mark="staff"
            left={<input className="spu-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('xStaffBuy.search')} />}
            right={<>
              <button type="button" className="pa-btn" onClick={() => staffFile.current && staffFile.current.click()}><Upload size={14} /> {t('xStaffBuy.importStaff')}</button>
              <input ref={staffFile} type="file" accept=".xlsx" hidden onChange={(e) => { upload('/setup/staff/import', e.target.files[0], (r) => t('xStaffBuy.staffImported', { n: r.rows, a: r.added, u: r.updated, m: r.missing.length ? ` ${t('xStaffBuy.missingList')}: ${r.missing.join(', ')}` : '' })); e.target.value = ''; }} />
              {addBtn(t('xStaffBuy.addStaff'), () => openStaff(null))}
            </>} columns={[
            { key: 'entity', label: t('xStaffBuy.entity'), get: (s) => s.entity },
            { key: 'name', label: t('xStaffBuy.employee'), get: (s) => s.name, cell: (s) => <>{s.name}{s.title ? <small> · {s.title}</small> : null}</> },
            { key: 'department', label: t('xStaffBuy.department'), get: (s) => s.department || '' },
            { key: 'joined', label: t('xStaffBuy.joined'), get: (s) => s.joined || '', cell: (s) => dmy(s.joined) },
            { key: 'eligible', label: t('xStaffBuy.eligible'), get: (s) => s.eligible || '', cell: (s) => dmy(s.eligible) },
            { key: 'senior', label: 'Sr Mgr+', get: (s) => (s.senior ? '✓' : ''), cell: (s) => (s.senior ? '✓' : '') },
            { key: 'account', label: t('xStaffBuy.account'), get: (s) => (s.userId ? (s.userName || String(s.userId)) : t('xStaffBuy.notLinked')),
              cell: (s) => (s.userId ? <>{s.userName}{s.email ? <small> · {s.email}</small> : null}</> : <span className="spu-dim">{t('xStaffBuy.notLinked')}</span>) },
            { key: 'active', label: t('xStaffBuy.active'), get: (s) => (s.active ? t('xStaffBuy.on') : t('xStaffBuy.off')), cell: (s) => <OnOff on={s.active} t={t} /> },
            { key: 'action', label: t('xStaffBuy.action'), cell: (s) => actions(() => openStaff(s), () => remove(`/setup/staff/${s.id}`, `${s.name} (${s.entity})`)) },
          ]} />
        </div>
      )}
      {part === 'prices' && (
        <div>
          <ToolTable t={t} rows={d.prices} rowKey={(p) => p.id} empty={t('xStaffBuy.noPrices')} mark="prices"
            left={<p className="spu-hint">{t('xStaffBuy.priceHint')}</p>}
            right={<>
              <button type="button" className="pa-btn" onClick={() => priceFile.current && priceFile.current.click()}><Upload size={14} /> {t('xStaffBuy.importPrices')}</button>
              <input ref={priceFile} type="file" accept=".xlsx" hidden onChange={(e) => { upload('/setup/prices/import', e.target.files[0], (r) => t('xStaffBuy.pricesImported', { n: r.rows, e: r.entities.join(', '), r: r.refused.length ? ` ${t('xStaffBuy.refused')}: ${r.refused.join('; ')}` : '' })); e.target.value = ''; }} />
              {addBtn(t('xStaffBuy.addPrice'), () => openPrice(null))}
            </>} columns={[
            { key: 'entity', label: t('xStaffBuy.entity'), get: (p) => p.entity },
            { key: 'code', label: t('xStaffBuy.itemCode'), get: (p) => p.code || '' },
            { key: 'name', label: t('xStaffBuy.product'), get: (p) => `${p.name}${p.color ? ` — ${p.color}` : ''}` },
            { key: 'category', label: t('xStaffBuy.category'), get: (p) => p.category || '' },
            { key: 'srp', label: 'SRP', num: true, get: (p) => p.srp, cell: (p) => p.srp.toLocaleString('en-US') },
            { key: 'pcur', label: t('xStaffBuy.currency'), get: (p) => p.currency || (d.entities.find((e) => e.entity === p.entity) || {}).currency || '' },
            { key: 'limited', label: t('xStaffBuy.limited'), get: (p) => (p.limited ? '✓' : ''), cell: (p) => (p.limited ? '✓' : '') },
            { key: 'action', label: t('xStaffBuy.action'), cell: (p) => actions(() => openPrice(p), () => remove(`/setup/price/${p.id}`, `${p.entity} · ${p.code} · ${p.name}`)) },
          ]} />
        </div>
      )}
      {part === 'fx' && (
        <ToolTable t={t} rows={d.fx || []} rowKey={fxKey} empty={t('xStaffBuy.noFx')} mark="fx"
          left={<p className="spu-hint">{t('xStaffBuy.fxHint')}</p>} right={addBtn(t('xStaffBuy.addFx'), () => openFx(null))} columns={[
          { key: 'year', label: t('xStaffBuy.fxYear'), get: (r) => r.year },
          { key: 'pair', label: t('xStaffBuy.fxPair'), get: (r) => `${r.from} → ${r.to}` },
          { key: 'rate', label: t('xStaffBuy.fxRate'), num: true, get: (r) => r.rate, cell: (r) => `1 ${r.from} = ${r.rate.toLocaleString('en-US', { maximumFractionDigits: 8 })} ${r.to}` },
          { key: 'note', label: t('xStaffBuy.fxNote'), get: (r) => r.note || '' },
          { key: 'by', label: t('xStaffBuy.fxBy'), get: (r) => r.by || '', cell: (r) => <>{r.by}{r.at ? <small> · {dmy(String(r.at).slice(0, 10))}</small> : null}</> },
          { key: 'action', label: t('xStaffBuy.action'), cell: (r) => actions(() => openFx(r), () => remove(`/setup/fx/${fxKey(r)}`, `${r.year} · 1 ${r.from} = ${r.rate} ${r.to}`)) },
        ]} />
      )}
      {form && <FormDialog key={form.title} title={form.title} fields={form.fields} initial={form.initial} onSubmit={form.submit} onClose={() => setForm(null)} t={t} />}
      {del && <ConfirmDelete what={del.what} onConfirm={del.run} onClose={() => setDel(null)} t={t} />}
    </div>
  );
}

/* ------------------------------------------------------------------ the page */
export default function StaffPurchase() {
  const { t } = useTranslation();
  const qs = new URLSearchParams(window.location.search);
  const [tab, setTab] = useState(qs.get('tab') || 'mine');
  const [me, setMe] = useState(null);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(qs.get('req') ? Number(qs.get('req')) : null);
  const [stamp, setStamp] = useState(0);
  const load = useCallback(() => call('GET', '/me').then(setMe).catch((e) => setErr(e.message)), []);
  useEffect(() => { load(); }, [load]);
  const canQueue = !!me && (me.isAdmin || me.approverOf.length > 0 || me.selfApprover);
  const canLedger = !!me && (me.ledgerAll || (me.managerOf || []).length > 0);
  const tabs = [['mine', ShoppingBag], ...(canQueue ? [['queue', CheckSquare]] : []), ...(canLedger ? [['ledger', BookOpen]] : []), ...(me?.isAdmin ? [['setup', Settings]] : [])];
  return (
    <div className="sp-page">
      <Header />
      <div className="sp-main sbc-page spu-page">
        <div className="page-sticky">
          <div className="sbc-head"><div><h2 className="pa-title">{t('xStaffBuy.title')}</h2><p className="spu-sub">{t('xStaffBuy.sub')}</p></div>
            <ModuleHelp steps={TOUR} tourKey="staffPurchase" ready={!!me} onBeforeTour={() => { setOpen(null); setTab('mine'); }} /></div>
          <div className="view-tabs" role="tablist">
            {tabs.map(([k, Icon]) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)} data-spu-tab={k}>
                <Icon size={16} aria-hidden="true" /> {t(`xStaffBuy.tab_${k}`)}
              </button>
            ))}
          </div>
        </div>
        {err && <p className="spu-err">{err}</p>}
        {!me && !err && <p>{t('xStaffBuy.loading')}</p>}
        {me && tab === 'mine' && <MineTab me={me} reload={load} t={t} onOpen={setOpen} />}
        {me && tab === 'queue' && canQueue && <QueueTab t={t} onOpen={setOpen} stamp={stamp} />}
        {me && tab === 'ledger' && canLedger && <LedgerTab t={t} year={me.year} />}
        {me && tab === 'setup' && me.isAdmin && <SetupTab t={t} />}
        {open && <RequestDialog id={open} t={t} onClose={() => setOpen(null)} onChanged={() => { setStamp((s) => s + 1); load(); }} />}
      </div>
    </div>
  );
}
